// Addresss.js - Customer Dropdown matched with Image Design
import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  FaMapMarkerAlt, FaSearch, FaPlus, FaTrash, FaCheckDouble, FaEllipsisV,
  FaSave, FaTimes, FaEdit, FaStore, FaChevronDown, FaUser, FaBuilding
} from 'react-icons/fa';
import { FaRegAddressBook } from "react-icons/fa";
import { RiDeleteBinFill } from 'react-icons/ri';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import './Addresss.css';

// Last loaded data of each shop is kept, so the page shows instantly
// on the next visit while fresh data loads in the background.
const AD_CACHE_PREFIX = 'addresses_cache_';
const AD_CUSTOMERS_PREFIX = 'addresses_customers_';
const AD_CITIES_PREFIX = 'addresses_cities_';
const AD_CUSTADDR_PREFIX = 'addresses_custaddr_';
const AD_SHOPS_KEY = 'addresses_shops_cache';

const readJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {}
};

const getUrlShopId = () => {
  const match = window.location.pathname.match(/\/shop\/(\d+)/);
  return match ? match[1] : null;
};

const parseCities = (data) => {
  let citiesData = [];
  if (data) {
    if (Array.isArray(data.cities)) {
      citiesData = data.cities;
    } else if (Array.isArray(data)) {
      citiesData = data;
    } else if (typeof data.cities === 'string') {
      try {
        citiesData = JSON.parse(data.cities);
      } catch {
        citiesData = data.cities.split(',').map(c => c.trim()).filter(c => c);
      }
    }
  }
  return citiesData;
};

const Addresss = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // The shop is known from the very first render (from the URL), and the saved
  // data of that shop is shown instantly while fresh data loads in the background.
  const initialShopId = getUrlShopId();
  const initialRef = useRef(null);
  if (initialRef.current === null) {
    const cachedAddresses = initialShopId ? readJson(`${AD_CACHE_PREFIX}${initialShopId}`) : null;
    const cachedCustomers = initialShopId ? readJson(`${AD_CUSTOMERS_PREFIX}${initialShopId}`) : null;
    const cachedCities = initialShopId ? readJson(`${AD_CITIES_PREFIX}${initialShopId}`) : null;
    const cachedShops = readJson(AD_SHOPS_KEY);
    initialRef.current = {
      addresses: Array.isArray(cachedAddresses) ? cachedAddresses : null,
      customers: Array.isArray(cachedCustomers) ? cachedCustomers : null,
      cities: Array.isArray(cachedCities) ? cachedCities : null,
      shops: Array.isArray(cachedShops) ? cachedShops : null
    };
  }

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAddresses, setSelectedAddresses] = useState([]);
  const [showActionDropdown, setShowActionDropdown] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [shops, setShops] = useState(initialRef.current.shops || []);
  const [selectedShopId, setSelectedShopId] = useState(initialShopId);
  const [selectedShopName, setSelectedShopName] = useState('');
  const [addressesList, setAddressesList] = useState(initialRef.current.addresses || []);
  // True only while there is nothing to show yet (so "No addresses found" never flashes)
  const [isLoading, setIsLoading] = useState(!initialRef.current.addresses);

  // ✅ City filter dropdown (header)
  const [filterCities, setFilterCities] = useState(initialRef.current.cities || []);
  const [selectedFilterCity, setSelectedFilterCity] = useState('');

  // ✅ NEW: Customer filter dropdown (header)
  const [selectedFilterCustomer, setSelectedFilterCustomer] = useState('');

  // City dropdown state (inside modal)
  const [cities, setCities] = useState(initialRef.current.cities || []);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const cityInputRef = useRef(null);
  const cityDropdownRef = useRef(null);

  // Customers state
  const [customers, setCustomers] = useState(initialRef.current.customers || []);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');

  // ✅ Customer's existing addresses (shown when customer is selected in modal)
  const [customerExistingAddresses, setCustomerExistingAddresses] = useState([]);
  // true once the addresses of the selected customer are known (so "No existing addresses" never flashes)
  const [customerAddressesReady, setCustomerAddressesReady] = useState(false);

  const addressesRequestRef = useRef(0);
  const customersRequestRef = useRef(0);
  const citiesRequestRef = useRef(0);
  const custAddrRequestRef = useRef(0);
  const prevShopRef = useRef(initialShopId);

  const BASE_URL = 'https://super-backend.hira-tech.co.uk';

  // Form State
  const [formData, setFormData] = useState({
    city: '',
    area: '',
    street_Number: '',
    house_Number: '',
    shop_id: '',
    customer_id: ''
  });

  // ✅ City counts are calculated from the loaded addresses
  const cityCounts = useMemo(() => {
    const counts = {};
    addressesList.forEach(address => {
      if (address.city) counts[address.city] = (counts[address.city] || 0) + 1;
    });
    return counts;
  }, [addressesList]);

  // SweetAlert helpers
  const showSuccessAlert = (message) => {
    Swal.fire({
      icon: 'success',
      title: 'Success!',
      text: message,
      timer: 2000,
      showConfirmButton: true,
      confirmButtonColor: '#3085d6'
    });
  };

  const showErrorAlert = (message) => {
    Swal.fire({
      icon: 'error',
      title: 'Error!',
      text: message,
      confirmButtonColor: '#d33'
    });
  };

  const showWarningAlert = (message) => {
    Swal.fire({
      icon: 'warning',
      title: 'Warning!',
      text: message,
      confirmButtonColor: '#f0ad4e'
    });
  };

  const showDeleteConfirmation = (message) => {
    return Swal.fire({
      title: 'Are you sure?',
      text: message,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });
  };

  // Get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');
    const csrfToken = document.cookie
      .split('; ')
      .find(row => row.startsWith('csrftoken='))
      ?.split('=')[1];

    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-CSRFToken': csrfToken || ''
    };
  };

  // ✅ Fetch addresses (runs in the background, only the latest request updates the screen)
  const fetchAddresses = async (shopIdArg) => {
    const shopId = shopIdArg || selectedShopId;
    if (!shopId) return;
    const requestId = ++addressesRequestRef.current;
    try {
      const response = await axios.get(`${BASE_URL}/user/addresses/`, {
        headers: getAuthHeaders(),
        params: { shop_id: shopId }
      });
      if (requestId !== addressesRequestRef.current) return;

      if (response.data && response.data.addresses) {
        setAddressesList(response.data.addresses);
        writeJson(`${AD_CACHE_PREFIX}${shopId}`, response.data.addresses);
      } else {
        setAddressesList([]);
        writeJson(`${AD_CACHE_PREFIX}${shopId}`, []);
      }
    } catch (error) {
      if (requestId !== addressesRequestRef.current) return;
      console.error('Error fetching addresses:', error);
      showErrorAlert('Error fetching addresses. Please try again.');
    } finally {
      if (requestId === addressesRequestRef.current) setIsLoading(false);
    }
  };

  // Fetch customers
  const fetchCustomers = async (shopIdArg) => {
    const shopId = shopIdArg || selectedShopId;
    if (!shopId) return;
    const requestId = ++customersRequestRef.current;
    try {
      const response = await axios.get(`${BASE_URL}/user/getAllcustomers/`, {
        headers: getAuthHeaders(),
        params: { shop_id: shopId }
      });
      if (requestId !== customersRequestRef.current) return;

      let customersData = [];
      if (Array.isArray(response.data)) customersData = response.data;
      else if (response.data.results) customersData = response.data.results;
      else if (response.data.customers) customersData = response.data.customers;

      setCustomers(customersData);
      writeJson(`${AD_CUSTOMERS_PREFIX}${shopId}`, customersData);
    } catch (error) {
      if (requestId !== customersRequestRef.current) return;
      console.error('Error fetching customers:', error);
    }
  };

  // Fetch the cities of the shop (used by the header filter and by the modal)
  const loadCities = async (shopIdArg) => {
    const shopId = shopIdArg || selectedShopId;
    if (!shopId) return;
    const requestId = ++citiesRequestRef.current;
    try {
      const response = await axios.get(
        `${BASE_URL}/user/shop-cities/?shop_id=${shopId}`,
        { headers: getAuthHeaders() }
      );
      if (requestId !== citiesRequestRef.current) return;

      const citiesData = parseCities(response.data);
      setFilterCities(citiesData);
      setCities(citiesData);
      writeJson(`${AD_CITIES_PREFIX}${shopId}`, citiesData);
    } catch (error) {
      if (requestId !== citiesRequestRef.current) return;
      console.error('Error fetching shop cities:', error);
    }
  };

  // Fetch the cities of one shop for the modal (edit)
  const fetchShopCities = async (shopId) => {
    if (!shopId) return;
    try {
      const response = await axios.get(
        `${BASE_URL}/user/shop-cities/?shop_id=${shopId}`,
        { headers: getAuthHeaders() }
      );
      if (response.data && response.data.cities) {
        setCities(response.data.cities);
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  // ✅ Fetch customer's existing addresses (for modal)
  const fetchCustomerAddresses = async (customerId) => {
    const requestId = ++custAddrRequestRef.current;

    if (!customerId) {
      setCustomerExistingAddresses([]);
      setCustomerAddressesReady(true);
      return;
    }

    const cached = readJson(`${AD_CUSTADDR_PREFIX}${customerId}`);
    if (Array.isArray(cached)) {
      setCustomerExistingAddresses(cached);
      setCustomerAddressesReady(true);
    } else {
      setCustomerExistingAddresses([]);
      setCustomerAddressesReady(false);
    }

    try {
      const response = await axios.get(
        `${BASE_URL}/user/customers/${customerId}/addresses/`,
        { headers: getAuthHeaders() }
      );
      if (requestId !== custAddrRequestRef.current) return;
      const list = response.data || [];
      setCustomerExistingAddresses(list);
      writeJson(`${AD_CUSTADDR_PREFIX}${customerId}`, list);
    } catch (error) {
      if (requestId !== custAddrRequestRef.current) return;
      console.error('Error fetching customer addresses:', error);
    } finally {
      if (requestId === custAddrRequestRef.current) setCustomerAddressesReady(true);
    }
  };

  const handleFilterCityChange = (e) => {
    setSelectedFilterCity(e.target.value);
  };

  // ✅ Handle Header Customer Filter Change
  const handleFilterCustomerChange = (e) => {
    setSelectedFilterCustomer(e.target.value);
  };

  // Initialize component (shops list is loaded once)
  useEffect(() => {
    const initializeComponent = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/user/shops/`, {
          headers: getAuthHeaders()
        });
        const shopsData = response.data || [];
        setShops(shopsData);
        writeJson(AD_SHOPS_KEY, shopsData);

        const match = location.pathname.match(/\/shop\/(\d+)/);
        if (match) {
          setSelectedShopId(match[1]);
        } else if (shopsData.length > 0) {
          const firstShopId = shopsData[0].shop_id;
          setSelectedShopId(String(firstShopId));
          navigate(`/shop/${firstShopId}/Addresss`, { replace: true });
        } else {
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error initializing:', error);
        if (!initialShopId) setIsLoading(false);
        showErrorAlert('Failed to load shops. Please try again.');
      }
    };
    initializeComponent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedShopId && shops.length > 0) {
      const shop = shops.find(s => String(s.shop_id) === String(selectedShopId));
      if (shop) setSelectedShopName(shop.shop_name);
      else setSelectedShopName(`Shop ${selectedShopId}`);
    }
  }, [selectedShopId, shops]);

  useEffect(() => {
    const match = location.pathname.match(/\/shop\/(\d+)/);
    if (match) {
      const shopIdFromUrl = match[1];
      if (shopIdFromUrl !== selectedShopId) setSelectedShopId(shopIdFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Data of the shop: show the saved data of the shop immediately, then refresh it.
  // Addresses, customers and cities load together instead of one after another.
  useEffect(() => {
    if (!selectedShopId) {
      setCities([]);
      setFilterCities([]);
      setSelectedFilterCity('');
      return;
    }
    const shopId = selectedShopId;

    if (prevShopRef.current !== shopId) {
      prevShopRef.current = shopId;
      const cachedAddresses = readJson(`${AD_CACHE_PREFIX}${shopId}`);
      const cachedCustomers = readJson(`${AD_CUSTOMERS_PREFIX}${shopId}`);
      const cachedCities = readJson(`${AD_CITIES_PREFIX}${shopId}`);
      if (Array.isArray(cachedAddresses)) {
        setAddressesList(cachedAddresses);
        setIsLoading(false);
      } else {
        setAddressesList([]);
        setIsLoading(true);
      }
      setCustomers(Array.isArray(cachedCustomers) ? cachedCustomers : []);
      const savedCities = Array.isArray(cachedCities) ? cachedCities : [];
      setFilterCities(savedCities);
      setCities(savedCities);
      setSelectedFilterCity('');
      setSelectedFilterCustomer('');
      setSelectedAddresses([]);
    }

    fetchAddresses(shopId);
    fetchCustomers(shopId);
    loadCities(shopId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShopId]);

  // Close city dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        cityDropdownRef.current &&
        !cityDropdownRef.current.contains(event.target) &&
        cityInputRef.current &&
        !cityInputRef.current.contains(event.target)
      ) {
        setShowCityDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter addresses based on City, Customer, and Search Term
  const filteredAddresses = addressesList.filter(address => {
    if (selectedFilterCity) {
      if (address.city !== selectedFilterCity) {
        return false;
      }
    }

    if (selectedFilterCustomer) {
      const addressCustomerId = address.customer || address.customer_id || (address.customer_details && address.customer_details.id);
      if (String(addressCustomerId) !== String(selectedFilterCustomer)) {
        return false;
      }
    }

    const searchLower = searchTerm.toLowerCase();
    return (
      (address.address_Id && String(address.address_Id).toLowerCase().includes(searchLower)) ||
      (address.city && address.city.toLowerCase().includes(searchLower)) ||
      (address.area && address.area.toLowerCase().includes(searchLower)) ||
      (address.shop_name && address.shop_name.toLowerCase().includes(searchLower)) ||
      (address.customer_name && address.customer_name.toLowerCase().includes(searchLower))
    );
  });

  const toggleAddressSelection = (address) => {
    setSelectedAddresses(prev => {
      const isSelected = prev.some(a => a.address_Id === address.address_Id);
      if (isSelected) {
        return prev.filter(a => a.address_Id !== address.address_Id);
      } else {
        return [...prev, address];
      }
    });
    setShowActionDropdown(false);
  };

  const handleSelectAll = () => {
    if (
      selectedAddresses.length === filteredAddresses.length &&
      filteredAddresses.length > 0
    ) {
      setSelectedAddresses([]);
    } else {
      setSelectedAddresses([...filteredAddresses]);
    }
    setShowActionDropdown(false);
  };

  const handleDeleteSelected = async () => {
    if (selectedAddresses.length === 0) {
      showWarningAlert('Please select addresses to delete');
      return;
    }
    const result = await showDeleteConfirmation(
      `You are about to delete ${selectedAddresses.length} selected address(es).`
    );
    if (result.isConfirmed) {
      try {
        const addressIds = selectedAddresses.map(addr => addr.address_Id);
        await axios.post(
          `${BASE_URL}/user/addresses/bulk-delete/`,
          { address_ids: addressIds },
          { headers: getAuthHeaders() }
        );
        const deletedCount = selectedAddresses.length;
        // the deleted rows disappear right away
        setAddressesList(prev => prev.filter(a => !addressIds.includes(a.address_Id)));
        setSelectedAddresses([]);
        setShowActionDropdown(false);
        showSuccessAlert(`${deletedCount} address(es) deleted successfully!`);
        fetchAddresses();
      } catch (error) {
        console.error('Error deleting addresses:', error);
        showErrorAlert('Error deleting addresses. Please try again.');
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCityInputChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, city: value });
    setShowCityDropdown(true);
  };

  const handleCitySelect = (city) => {
    setFormData({ ...formData, city: city });
    setShowCityDropdown(false);
  };

  const toggleCityDropdown = () => setShowCityDropdown(!showCityDropdown);

  const clearCity = () => {
    setFormData({ ...formData, city: '' });
    setShowCityDropdown(false);
    if (cityInputRef.current) {
      cityInputRef.current.focus();
    }
  };

  const filteredCities = cities.filter(city =>
    city.toLowerCase().includes(formData.city.toLowerCase())
  );

  // Handle customer selection change inside modal
  const handleCustomerChange = async (customerId) => {
    setFormData({ ...formData, customer_id: customerId });
    setSelectedCustomerId(customerId);

    if (customerId) {
      await fetchCustomerAddresses(customerId);
    } else {
      custAddrRequestRef.current++;
      setCustomerExistingAddresses([]);
    }
  };

  const fillFromExistingAddress = (addr) => {
    setFormData({
      ...formData,
      city: addr.city || '',
      area: addr.area || '',
      street_Number: addr.street_Number || '',
      house_Number: addr.house_Number || ''
    });
    showSuccessAlert('Address fields filled from existing address. You can modify and save as new.');
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingAddress(null);
    setFormData({
      city: '',
      area: '',
      street_Number: '',
      house_Number: '',
      shop_id: '',
      customer_id: ''
    });
    setSelectedCustomerId('');
    custAddrRequestRef.current++;
    setCustomerExistingAddresses([]);
    setCustomerAddressesReady(false);
    setShowCityDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.city || !formData.area) {
      showWarningAlert('Please fill in all required fields (City, Area)');
      return;
    }

    if (!selectedShopId) {
      showWarningAlert('No shop selected');
      return;
    }

    const addressData = {
      city: formData.city,
      area: formData.area,
      street_Number: formData.street_Number || '',
      house_Number: formData.house_Number || '',
      shop_id: Number(selectedShopId),
    };

    if (formData.customer_id) {
      addressData.customer_id = Number(formData.customer_id);
    }

    try {
      if (editingAddress) {
        await axios.put(
          `${BASE_URL}/user/addresses/${editingAddress.address_Id}/`,
          addressData,
          { headers: getAuthHeaders() }
        );
        showSuccessAlert('Address updated successfully!');
      } else {
        await axios.post(
          `${BASE_URL}/user/addresses/`,
          addressData,
          { headers: getAuthHeaders() }
        );
        showSuccessAlert('Address added successfully!');
      }

      closeModal();
      fetchAddresses();
    } catch (error) {
      console.error('Error saving address:', error);
      console.error('Backend Error Details:', error.response?.data); // ✅ Added to see exact error in console

      // ✅ IMPROVED: Extract exact backend error message (JSON) to show in the popup
      let errorMsg = 'Error saving address. Please try again.';
      const errorData = error.response?.data;

      if (errorData) {
        if (typeof errorData === 'string') {
          errorMsg = errorData;
        } else if (errorData.error) {
          errorMsg = errorData.error;
        } else if (errorData.message) {
          errorMsg = errorData.message;
        } else if (errorData.detail) {
          errorMsg = errorData.detail;
        } else {
          // If it's an object with field errors (e.g., {"city": ["This field is required."]})
          const firstKey = Object.keys(errorData)[0];
          if (firstKey && Array.isArray(errorData[firstKey])) {
            errorMsg = `${firstKey}: ${errorData[firstKey][0]}`;
          } else if (typeof errorData === 'object') {
            errorMsg = JSON.stringify(errorData);
          }
        }
      }

      showErrorAlert(errorMsg);
    }
  };

  const handleEdit = async (address) => {
    setEditingAddress(address);
    setFormData({
      city: address.city || '',
      area: address.area || '',
      street_Number: address.street_Number || '',
      house_Number: address.house_Number || '',
      shop_id: address.shop_id || '',
      customer_id: address.customer || ''
    });
    setSelectedCustomerId(address.customer || '');
    setShowModal(true);
    if (address.shop_id) fetchShopCities(address.shop_id);

    if (address.customer) {
      await fetchCustomerAddresses(address.customer);
    }
  };

  const handleDeleteSingle = async (address) => {
    const addressId = `ADD-${String(address.address_Id).padStart(3, '0')}`;
    const result = await showDeleteConfirmation(
      `You are about to delete address ${addressId}.`
    );
    if (result.isConfirmed) {
      try {
        await axios.delete(
          `${BASE_URL}/user/addresses/${address.address_Id}/`,
          { headers: getAuthHeaders() }
        );
        showSuccessAlert(`Address ${addressId} deleted successfully!`);
        // the deleted row disappears right away
        setAddressesList(prev => prev.filter(a => a.address_Id !== address.address_Id));
        setSelectedAddresses(prev =>
          prev.filter(a => a.address_Id !== address.address_Id)
        );
        fetchAddresses();
      } catch (error) {
        console.error('Error deleting address:', error);
        showErrorAlert('Error deleting address. Please try again.');
      }
    }
  };

  const getCustomerDisplayName = (customer) => {
    const firstName = customer.first_Name || customer.firstName || '';
    const lastName = customer.last_Name || customer.lastName || '';
    const email = customer.email || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName ? `${fullName} (${email})` : email;
  };

  const getCustomerName = (address) => {
    if (address.customer_name && address.customer_name.trim()) {
      return address.customer_name;
    }
    if (address.customer_details) {
      const firstName = address.customer_details.first_Name || '';
      const lastName = address.customer_details.last_Name || '';
      const email = address.customer_details.email || '';
      const fullName = `${firstName} ${lastName}`.trim();
      return fullName || email || 'No Customer';
    }
    if (address.customer) {
      const foundCustomer = customers.find(
        c => String(c.id || c.user_id || c.customer_id) === String(address.customer)
      );
      if (foundCustomer) {
        const firstName = foundCustomer.first_Name || foundCustomer.firstName || '';
        const lastName = foundCustomer.last_Name || foundCustomer.lastName || '';
        const email = foundCustomer.email || '';
        const fullName = `${firstName} ${lastName}`.trim();
        return fullName || email || 'No Customer';
      }
    }
    return 'No Customer';
  };

  return (
    <div className="addresses-container">
      {/* HEADER */}
      <div className="addresses-header-section">
        <div className="addresses-header-left">
          <FaRegAddressBook className="addresses-header-icon" />
          <h2 className="addresses-header-title">Addresses Management</h2>
          <span className="shop-city-badge">
            <FaMapMarkerAlt className="shop-city-icon" />
            <span className="shop-city-name">
              {selectedFilterCity
                ? `${selectedFilterCity} (${cityCounts[selectedFilterCity] || 0})`
                : `All Cities (${filteredAddresses.length})`}
            </span>
          </span>
        </div>

        <div className="addresses-header-right">

          {/* ✅ NEW: Customer Dropdown (matched with image) */}
          <div className="customer-dropdown-wrapper">
            <select
              className="customer-select-dropdown"
              value={selectedFilterCustomer}
              onChange={handleFilterCustomerChange}
            >
              <option value="">
                Select Customer
              </option>
              {customers.map((customer) => (
                <option
                  key={customer.id || customer.user_id || customer.customer_id}
                  value={customer.id || customer.user_id || customer.customer_id}
                >
                  {getCustomerDisplayName(customer)}
                </option>
              ))}
            </select>
          </div>

          {/* City Dropdown */}
          <div className="city-dropdown-wrapper">
            <select
              className="city-select-dropdown"
              value={selectedFilterCity}
              onChange={handleFilterCityChange}
              disabled={filterCities.length === 0}
            >
              <option value="">
                All Cities
              </option>
              {filterCities.map((city, index) => (
                <option key={index} value={city}>
                  {city} ({cityCounts[city] || 0})
                </option>
              ))}
            </select>
          </div>

          {/* Search */}
          <div className="search-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by Address ID, City, Area, Customer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          {/* Select All */}
          <button className="select-all-btn" onClick={handleSelectAll}>
            <FaCheckDouble />
            <span>
              {selectedAddresses.length === filteredAddresses.length &&
              filteredAddresses.length > 0
                ? 'Deselect All'
                : 'Select All'}
            </span>
          </button>

          {/* Add Address */}
          <button
            className="add-address-btn"
            onClick={() => {
              setEditingAddress(null);
              setFormData({
                city: '',
                area: '',
                street_Number: '',
                house_Number: '',
                shop_id: selectedShopId || '',
                customer_id: selectedFilterCustomer || ''
              });
              setSelectedCustomerId(selectedFilterCustomer || '');
              setCustomerExistingAddresses([]);
              setCustomerAddressesReady(false);
              if(selectedFilterCustomer) fetchCustomerAddresses(selectedFilterCustomer);
              setShowModal(true);
            }}
          >
            <FaPlus />
            <span>ADD ADDRESS</span>
          </button>
        </div>
      </div>

      <div className="addresses-content">
        {selectedAddresses.length > 0 && (
          <div className="selected-summary">
            <div className="summary-content">
              <span className="summary-icon">✓</span>
              <span className="summary-text">
                {selectedAddresses.length} address(es) selected
              </span>
              <div className="action-dropdown-container">
                <button
                  className="action-dropdown-btn"
                  onClick={() => setShowActionDropdown(!showActionDropdown)}
                >
                  <FaEllipsisV />
                  <span>Actions</span>
                </button>
                {showActionDropdown && (
                  <div className="action-dropdown-menu">
                    <button
                      className="dropdown-item delete-item"
                      onClick={handleDeleteSelected}
                    >
                      <FaTrash />
                      <span>Delete Selected ({selectedAddresses.length})</span>
                    </button>
                  </div>
                )}
              </div>
              <button
                className="clear-selection"
                onClick={() => {
                  setSelectedAddresses([]);
                  setShowActionDropdown(false);
                }}
              >
                Clear all
              </button>
            </div>
          </div>
        )}

        <div className="addresses-table-container">
          <div className="addresses-table-wrapper">
            <table className="addresses-table">
              <thead>
                <tr>
                  <th style={{ width: '50px' }}>
                    <input
                      type="checkbox"
                      checked={
                        selectedAddresses.length === filteredAddresses.length &&
                        filteredAddresses.length > 0
                      }
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>Address ID</th>
                  <th>City</th>
                  <th>Area</th>
                  <th>Street No.</th>
                  <th>House No.</th>
                  <th>Customer</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {/* Nothing is shown until the first load is done, so "No addresses found" never flashes */}
                {isLoading ? (
                  <tr>
                    <td colSpan="8" style={{ height: '200px' }}></td>
                  </tr>
                ) : filteredAddresses.length > 0 ? (
                  filteredAddresses.map((address) => (
                    <tr
                      key={address.address_Id}
                      className={
                        selectedAddresses.some(
                          a => a.address_Id === address.address_Id
                        )
                          ? 'selected'
                          : ''
                      }
                    >
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedAddresses.some(
                            a => a.address_Id === address.address_Id
                          )}
                          onChange={() => toggleAddressSelection(address)}
                        />
                      </td>
                      <td className="address-id-cell">
                        ADD-{String(address.address_Id).padStart(3, '0')}
                      </td>
                      <td className="address-city-cell">{address.city || '-'}</td>
                      <td>{address.area || '-'}</td>
                      <td>{address.street_Number || '-'}</td>
                      <td>{address.house_Number || '-'}</td>
                      <td className="address-customer-cell">
                        {getCustomerName(address) !== 'No Customer' ? (
                          <span className="customer-text">
                            {getCustomerName(address)}
                          </span>
                        ) : (
                          <span className="no-customer">No Customer</span>
                        )}
                      </td>
                      <td className="actions-cell">
                        <div className="actions-cell-inner">
                          <button
                            className="edit-address-btn"
                            onClick={() => handleEdit(address)}
                            title="Edit Address"
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="delete-address-btn"
                            onClick={() => handleDeleteSingle(address)}
                            title="Delete Address"
                          >
                            <RiDeleteBinFill />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="no-data-cell">
                      <FaBuilding className="no-data-icon" />
                      <p>
                        {selectedFilterCity || selectedFilterCustomer
                          ? `No addresses found for the selected filters.`
                          : `No addresses found for ${selectedShopName}.`}
                      </p>
                      <button
                        className="add-first-btn"
                        onClick={() => {
                          setEditingAddress(null);
                          setFormData({
                            city: selectedFilterCity || '',
                            area: '',
                            street_Number: '',
                            house_Number: '',
                            shop_id: selectedShopId || '',
                            customer_id: selectedFilterCustomer || ''
                          });
                          setSelectedCustomerId(selectedFilterCustomer || '');
                          setCustomerExistingAddresses([]);
                          setCustomerAddressesReady(false);
                          if(selectedFilterCustomer) fetchCustomerAddresses(selectedFilterCustomer);
                          setShowModal(true);
                        }}
                      >
                        <FaPlus /> Add Your First Address
                      </button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div
            className="modal-container"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>
                {editingAddress ? 'Edit Address' : 'Add New Address'}
              </h3>
              <button className="modal-close-btn" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              {/* SHOP SECTION */}
              <div className="modal-shop-section">
                <label className="shop-label">SHOP</label>
                <div className="shop-display-box">
                  <span className="shop-name-text">{selectedShopName}</span>
                </div>
              </div>

              {/* City + Customer */}
              <div className="modal-form-row">
                <div className="modal-form-group">
                  <label>City *</label>
                  <div className="city-combobox-container" ref={cityDropdownRef}>
                    <div className="city-input-wrapper">
                      <input
                        ref={cityInputRef}
                        type="text"
                        placeholder="Type city or select"
                        value={formData.city}
                        onChange={handleCityInputChange}
                        onFocus={() => setShowCityDropdown(true)}
                        className="city-combobox-input"
                        autoComplete="off"
                        required
                      />
                      <div className="city-combobox-actions">
                        {formData.city && (
                          <button
                            type="button"
                            className="city-clear-btn"
                            onClick={clearCity}
                            title="Clear city"
                          >
                            <FaTimes />
                          </button>
                        )}
                        <button
                          type="button"
                          className="city-dropdown-toggle"
                          onClick={toggleCityDropdown}
                          title="Toggle city dropdown"
                        >
                          <FaChevronDown
                            className={showCityDropdown ? 'rotate-up' : ''}
                          />
                        </button>
                      </div>
                    </div>

                    {showCityDropdown && (
                      <div className="city-dropdown-list">
                        {filteredCities.length > 0 ? (
                          filteredCities.map((city, index) => (
                            <div
                              key={index}
                              className={`city-dropdown-item ${
                                city === formData.city ? 'selected' : ''
                              }`}
                              onClick={() => handleCitySelect(city)}
                            >
                              {city}
                            </div>
                          ))
                        ) : (
                          <div className="city-dropdown-empty">
                            {formData.city ? (
                              <span>
                                Type "{formData.city}" to add custom city
                              </span>
                            ) : (
                              <span>
                                No cities found. Type to add custom city
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="modal-form-group">
                  <label>Customer</label>
                  <div className="input-wrapper">
                    <select
                      className="customer-select-input"
                      value={formData.customer_id}
                      onChange={(e) => handleCustomerChange(e.target.value)}
                    >
                      <option value="">-- No Customer --</option>
                      {customers.length > 0 ? (
                        customers.map((customer) => (
                          <option
                            key={
                              customer.id ||
                              customer.user_id ||
                              customer.customer_id
                            }
                            value={
                              customer.id ||
                              customer.user_id ||
                              customer.customer_id
                            }
                          >
                            {getCustomerDisplayName(customer)}
                          </option>
                        ))
                      ) : (
                        <option value="" disabled>
                          No customers available
                        </option>
                      )}
                    </select>
                  </div>
                </div>
              </div>

              {/* Show customer's existing addresses */}
              {selectedCustomerId && (
                <div className="modal-form-group full-width">
                  <label>
                    Customer's Existing Addresses
                  </label>
                  {customerExistingAddresses.length > 0 ? (
                    <div className="existing-addresses-list">
                      {customerExistingAddresses.map((addr) => (
                        <div
                          key={addr.address_Id}
                          className={`existing-address-item ${
                            editingAddress && editingAddress.address_Id === addr.address_Id
                              ? 'current-editing'
                              : ''
                          }`}
                          onClick={() => fillFromExistingAddress(addr)}
                          title="Click to copy this address to the form"
                        >
                          <span className="address-badge">
                            ADD-{String(addr.address_Id).padStart(3, '0')}
                          </span>
                          <span className="address-detail-text">
                            {addr.city}
                            {addr.area && `, ${addr.area}`}
                            {addr.street_Number && `, St: ${addr.street_Number}`}
                            {addr.house_Number && `, House: ${addr.house_Number}`}
                          </span>
                          {editingAddress && editingAddress.address_Id === addr.address_Id && (
                            <span className="editing-badge">Currently Editing</span>
                          )}
                        </div>
                      ))}
                      <p className="existing-address-hint">
                        💡 Click any address above to copy its details into the form
                      </p>
                    </div>
                  ) : customerAddressesReady ? (
                    <div className="no-existing-addresses">
                      No existing addresses for this customer
                    </div>
                  ) : null}
                </div>
              )}

              {/* Area + Street */}
              <div className="modal-form-row">
                <div className="modal-form-group">
                  <label>Area *</label>
                  <div className="input-wrapper">
                    <input
                      type="text"
                      name="area"
                      value={formData.area}
                      onChange={handleInputChange}
                      placeholder="Enter area name"
                      required
                    />
                  </div>
                </div>
                <div className="modal-form-group">
                  <label>Street Number</label>
                  <div className="input-wrapper">
                    <input
                      type="text"
                      name="street_Number"
                      value={formData.street_Number}
                      onChange={handleInputChange}
                      placeholder="e.g., 10, 22A"
                    />
                  </div>
                </div>
              </div>

              {/* House Number */}
              <div className="modal-form-row">
                <div className="modal-form-group">
                  <label>House Number</label>
                  <div className="input-wrapper">
                    <input
                      type="text"
                      name="house_Number"
                      value={formData.house_Number}
                      onChange={handleInputChange}
                      placeholder="e.g., 10, 22A, Flat 5"
                    />
                  </div>
                </div>
              </div>

              <div className="modal-form-actions">
                <button
                  type="button"
                  className="modal-cancel-btn"
                  onClick={closeModal}
                >
                  Cancel
                </button>
                <button type="submit" className="modal-submit-btn">
                  <span>
                    {editingAddress ? 'Update Address' : 'Save Address'}
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Addresss;